import React, { useEffect, useState } from "react";
import { taxIdState } from "../utils/identity";
import { Supplier, SupplierUpsert } from "../types";
import { purchasingService } from "../services/dataService";
import "./SendDocumentModal.css";
import "../pages/Receivables.css";
import "../pages/Purchasing.css";

interface Props {
  supplier?: Supplier | null;
  onClose: () => void;
  onSaved: () => void;
}

const empty: SupplierUpsert = {
  name: "", code: "", taxNumber: "", taxOffice: "", contactPerson: "", phone: "", email: "",
  address: "", paymentTermDays: 30, currency: "TRY", note: "", isActive: true,
};

const SupplierModal: React.FC<Props> = ({ supplier, onClose, onSaved }) => {
  const [f, setF] = useState<SupplierUpsert>(supplier ? {
    name: supplier.name, code: supplier.code ?? "", taxNumber: supplier.taxNumber ?? "", taxOffice: supplier.taxOffice ?? "",
    contactPerson: supplier.contactPerson ?? "", phone: supplier.phone ?? "", email: supplier.email ?? "", address: supplier.address ?? "",
    paymentTermDays: supplier.paymentTermDays, currency: supplier.currency, note: supplier.note ?? "", isActive: supplier.isActive,
  } : empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const set = (k: keyof SupplierUpsert, v: any) => { setF({ ...f, [k]: v }); setError(""); };

  const save = async () => {
    if (!f.name.trim()) return setError("Tedarikçi adı zorunludur.");
    const tid = taxIdState(f.taxNumber ?? "");
    if (tid === "incomplete") return setError("VKN 10 hane, TCKN 11 hane olmalı.");
    if (tid === "invalid") return setError("VKN / TCKN geçersiz; haneleri kontrol edin.");
    setSaving(true); setError("");
    try {
      if (supplier) await purchasingService.updateSupplier(supplier.supplierId, f);
      else await purchasingService.createSupplier(f);
      onSaved(); onClose();
    } catch (err: any) {
      const d = err?.response?.data;
      setError(typeof d === "string" ? d : d?.message || "Kaydedilemedi.");
    } finally { setSaving(false); }
  };

  return (
    <div className="ic-overlay rc-page" onClick={() => !saving && onClose()}>
      <div className="ic-panel" style={{ maxWidth: "44rem" }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="ic-bar">
          <div className="ic-bar-left"><h2>{supplier ? "Tedarikçiyi Düzenle" : "Yeni Tedarikçi"}</h2></div>
          <button className="ic-close" onClick={onClose} aria-label="Kapat" disabled={saving}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>
        <div className="ic-body">
          {error && <div className="ic-alert error">{error}</div>}
          <div className="ic-doc" style={{ padding: "1.25rem 1.5rem" }}>
            <div className="rc-form">
              <div className="rc-field full"><label>Tedarikçi Adı</label><input className="ic-in" value={f.name} autoFocus onChange={(e) => set("name", e.target.value)} /></div>
              <div className="rc-field"><label>Kod</label><input className="ic-in" value={f.code ?? ""} maxLength={20} onChange={(e) => set("code", e.target.value)} /></div>
              <div className="rc-field"><label>İlgili Kişi</label><input className="ic-in" value={f.contactPerson ?? ""} onChange={(e) => set("contactPerson", e.target.value)} /></div>
              <div className="rc-field"><label>VKN / TCKN{taxIdState(f.taxNumber ?? "") === "invalid" && <em className="rc-field-err"> · geçersiz</em>}{taxIdState(f.taxNumber ?? "") === "ok" && <em className="rc-field-ok"> · doğrulandı</em>}</label><input className={`ic-in ${taxIdState(f.taxNumber ?? "") === "invalid" ? "is-invalid" : ""}`} inputMode="numeric" value={f.taxNumber ?? ""} maxLength={11} onChange={(e) => set("taxNumber", e.target.value.replace(/\D/g, ""))} /></div>
              <div className="rc-field"><label>Vergi Dairesi</label><input className="ic-in" value={f.taxOffice ?? ""} onChange={(e) => set("taxOffice", e.target.value)} /></div>
              <div className="rc-field"><label>Telefon</label><input className="ic-in" value={f.phone ?? ""} onChange={(e) => set("phone", e.target.value)} /></div>
              <div className="rc-field"><label>E-posta</label><input className="ic-in" type="email" value={f.email ?? ""} onChange={(e) => set("email", e.target.value)} /></div>
              <div className="rc-field full"><label>Adres</label><input className="ic-in" value={f.address ?? ""} maxLength={300} onChange={(e) => set("address", e.target.value)} /></div>
              <div className="rc-field"><label>Vade (gün)</label><input className="ic-in num" type="number" min={0} max={365} value={f.paymentTermDays} onChange={(e) => set("paymentTermDays", parseInt(e.target.value || "0", 10))} /></div>
              <div className="rc-field"><label>Para Birimi</label>
                <select className="ic-in" value={f.currency} onChange={(e) => set("currency", e.target.value)}>
                  <option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
                </select>
              </div>
              <div className="rc-field full"><label>Not</label><input className="ic-in" value={f.note ?? ""} maxLength={500} onChange={(e) => set("note", e.target.value)} /></div>
              {supplier && (
                <div className="rc-field full">
                  <label className="rc-check" style={{ textTransform: "none", letterSpacing: 0, fontSize: "0.8125rem", color: "#101B33" }}>
                    <input type="checkbox" checked={f.isActive} onChange={(e) => set("isActive", e.target.checked)} /> Aktif tedarikçi
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
        <footer className="ic-actions">
          <span className="ic-actions-summary">{supplier ? supplier.name : "Yeni kayıt"}</span>
          <div className="ic-actions-btns">
            <button className="ic-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button className="ic-btn primary" onClick={save} disabled={saving}>{saving ? "Kaydediliyor…" : "Kaydet"}</button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SupplierModal;
